import { Project } from '@models/project.model';
import { ClientProjectOverview } from '@models/client.model';
import { SubcontractorHealth } from '@models/subcontractor.model';
import { ProjectStatus } from '@models/projectStatus.model';
import { formatDateOnly } from '@utils/formatDate';

/** Fills `{key}` placeholders in a catalog template. */
export const fillTemplate = (
  template: string,
  values: Record<string, string | number>,
): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );

const startTime = (project: Project): number => {
  if (!project.startDate) return Number.MAX_SAFE_INTEGER;
  const time = new Date(project.startDate).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

const rank = (project: Project): number => {
  const state = project.state?.toLowerCase();
  if (state === ProjectStatus.ONGOING) return 0;
  if (state === ProjectStatus.COMPLETED) return 2;
  return 1;
};

const attentionTier = (overview?: ClientProjectOverview): number => {
  if (!overview) return 2;
  if (overview.health === SubcontractorHealth.BEHIND) return 0;
  if (overview.awaitingClientApprovalCount + overview.openIncidentsCount > 0)
    return 1;
  return 2;
};

/**
 * Sprint 44, story-226. BEHIND projects first, then projects waiting on the client or carrying an
 * open incident (ties inside those two tiers: more overdue first), then the existing order:
 * ONGOING by start date ascending, the rest, completed last. Without overviews (loading or
 * failed) the order is exactly the pre-sprint-44 one. Does not mutate the input.
 */
export const sortClientProjects = (
  projects: Project[],
  overviews?: ClientProjectOverview[],
): Project[] => {
  const byId = new Map((overviews ?? []).map((o) => [o.projectId, o]));
  const overviewOf = (project: Project) =>
    project.projectId ? byId.get(project.projectId) : undefined;
  return [...projects].sort((a, b) => {
    const oa = overviewOf(a);
    const ob = overviewOf(b);
    const tierA = attentionTier(oa);
    const tierB = attentionTier(ob);
    if (tierA !== tierB) return tierA - tierB;
    if (tierA < 2) {
      const overdueDiff = (ob?.overdueCount ?? 0) - (oa?.overdueCount ?? 0);
      if (overdueDiff !== 0) return overdueDiff;
    }
    return rank(a) - rank(b) || startTime(a) - startTime(b);
  });
};

interface DateTemplates {
  range: string;
  starts: string;
  none: string;
}

/** The dates line under the address: both dates, only a start, or "Dates to be set". */
export const formatClientProjectDates = (
  project: Pick<Project, 'startDate' | 'endDate'>,
  templates: DateTemplates,
): string => {
  const start = formatDateOnly(project.startDate);
  const end = formatDateOnly(project.endDate);
  if (start && end) return fillTemplate(templates.range, { start, end });
  if (start) return fillTemplate(templates.starts, { start });
  return templates.none;
};
